import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import {
  combineLatest,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { AuthService } from '../../../../services/auth.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { AlertType } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';

@Component({
  selector: 'app-list-archive-lessons',
  standalone: true,
  imports: [
    AsyncPipe,
    MomentPipe,
    RouterLink,
    BtnMoreComponent,
    BtnChipComponent,
    BtnFabComponent,
    BtnMoreComponent,
    ListUtilsBarComponent,
  ],
  templateUrl: './list-archive-lessons.component.html',
  styleUrl: './list-archive-lessons.component.css',
})
export class ListArchiveLessonsComponent {
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private store: Store<AppState> = inject(Store<AppState>);
  logger: NGXLogger = inject(NGXLogger);
  toastr: ToastrService = inject(ToastrService);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);

  groupId: string | null = this.route.root.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.root.snapshot.paramMap.get('facultyId');
  gradeId: string | null = this.route.root.snapshot.paramMap.get('gradeId');
  academicYear: number | undefined = undefined;

  user: UserModel | null = null;
  teacher: SchoolMember | null | undefined = null;
  group: Group | null | undefined;
  grade: Grade | null | undefined = null;

  archivedLessons$: Observable<GradeLesson[] | null | undefined> =
    new Observable();

  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();

  membershipStatus = MembershipStatus;
  lessonStatus = GradeLessonStatus;
  rolesType = SchoolRoles;
  utils = UtilsService;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        gradeId: pMap.get('gradeId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.loading$ = this.store
      .select(lessonSelector.loading)
      .pipe(takeUntil(this.exit$));

    this.inProgress$ = this.store
      .pipe(select(lessonSelector.inProgress))
      .pipe(takeUntil(this.exit$));

    this.myRoles$ = this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$));
  }

  ngOnInit(): void {
    this.params$.subscribe(({ groupId, facultyId, gradeId, academicYear }) => {
      this.groupId = groupId;
      this.facultyId = facultyId;
      this.gradeId = gradeId;
      this.academicYear = academicYear;

      this.logger.debug(
        `ListArchiveLessonsComponent :: Received route params groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, academicYear=${academicYear}`,
      );

      if (groupId) {
        this.loadData(groupId, facultyId, gradeId, academicYear);
      }
    });
  }

  private loadData(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    academicYear?: number,
  ) {
    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        const effectiveYear = academicYear ?? group?.academicYear;
        this.academicYear = effectiveYear;

        this.logger.debug(
          `ListArchiveLessonsComponent :: Loaded group ${group?.name} with academicYear=${group?.academicYear}. Effective academic year for sessions: ${effectiveYear}`,
        );

        if (effectiveYear) {
          this.initDataStreams(groupId, facultyId, gradeId, effectiveYear);
        }
      });
  }

  private initDataStreams(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    year: number,
  ) {
    this.logger.debug(
      `ListArchiveLessonsComponent :: Initializing data streams with groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, year=${year}`,
    );

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    if (gradeId) {
      this.setupGradeAndLessons(groupId, gradeId, year);
    } else if (facultyId) {
      this.store
        .select(memberSelector.getSchoolMemberById(facultyId, groupId))
        .pipe(
          filter((m) => !!m),
          takeUntil(this.exit$),
        )
        .subscribe((teacher) => {
          this.teacher = teacher;
          this.logger.debug(
            `ListArchiveLessonsComponent :: Loaded teacher ${teacher?.name} with gradeId=${teacher?.gradeId}`,
          );
          if (teacher?.gradeId) {
            this.setupGradeAndLessons(groupId, teacher.gradeId, year);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  setupGradeAndLessons(groupId: string, gradeId: string, academicYear: number) {
    this.logger.debug(
      `ListArchiveLessonsComponent :: loadContent :: gradeId: ${this.gradeId}, facultyId: ${this.facultyId}, groupId: ${this.groupId}, academicYear: ${this.academicYear}`,
    );
    this.archivedLessons$ = this.store.select(
      lessonSelector.getGradeLessonsByGradeId({
        gradeId: gradeId,
        groupId: groupId,
        academicYear: this.academicYear ?? this.group?.academicYear!,
        lessonStatus: [GradeLessonStatus.archived],
      }),
    );

    // 2. Load the Grade object for UI labels/titles
    this.store
      .select(
        gradeSelector.getGradeById({
          gradeId,
          groupId: this.groupId!,
          academicYear,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((grade) => {
        this.grade = grade;
        this.logger.debug(
          `ListArchiveLessonsComponent :: Loaded grade ${grade?.gradeName} with teacherId=${grade?.teacherId}`,
        );

        // If the grade has a teacherId we didn't have before, load that faculty too
        if (grade?.teacherId) {
          this.store
            .select(
              memberSelector.getSchoolMemberById(grade.teacherId, groupId),
            )
            .pipe(take(1))
            .subscribe((t) => (this.teacher = t));
        }
      });

    // 3. Trigger the initial Dispatch if the store is empty
    this.archivedLessons$.pipe(take(1)).subscribe((lessons) => {
      if (!lessons || lessons.length === 0) {
        this.logger.debug(
          `ListArchiveLessonsComponent :: No lessons found for gradeId=${gradeId}, year=${academicYear}. Dispatching getLessons action.`,
        );
        this.store.dispatch(
          lessonActions.getGradeLessons({
            groupId,
            gradeId,
            academicYear: academicYear,
            status: [GradeLessonStatus.archived],
          }),
        );
      }
    });
  }

  addMore() {
    if (this.groupId === null || this.grade?.id === null) return;
    this.archivedLessons$.pipe(take(1)).subscribe((lessons) => {
      this.store.dispatch(
        lessonActions.getGradeLessons({
          groupId: this.groupId!,
          gradeId: this.grade?.id!,
          academicYear: this.group?.academicYear!,
          status: [GradeLessonStatus.archived],
          lastRecord:
            (lessons ?? [])?.length > 0
              ? lessons![lessons!.length - 1]?.modified
              : undefined,
        }),
      );
    });
  }

  onRefresh() {
    if (this.groupId === null || this.grade?.id === null) return;
    this.store.dispatch(
      lessonActions.getGradeLessons({
        groupId: this.groupId!,
        gradeId: this.grade?.id!,
        academicYear: this.group?.academicYear!,
        status: [GradeLessonStatus.open, GradeLessonStatus.closed],
      }),
    );
  }

  addMoreArchiveLessons() {
    if (this.groupId === null || this.grade?.id === null) return;
    this.archivedLessons$.pipe(take(1)).subscribe((lessons) => {
      this.store.dispatch(
        lessonActions.getGradeLessons({
          groupId: this.groupId!,
          gradeId: this.grade?.id!,
          academicYear: this.academicYear!,
          status: [GradeLessonStatus.archived],
          lastRecord:
            (lessons ?? [])?.length > 0
              ? lessons![lessons!.length - 1]?.modified
              : undefined,
        }),
      );
    });
  }

  onToggleLessonArchival(lesson: GradeLesson, action: GradeLessonStatus) {
    if (
      action == GradeLessonStatus.archived &&
      lesson.lessonStatus !== GradeLessonStatus.closed
    ) {
      this.utilsService.onShowAlert(
        {
          title: 'Lesson Not Closed',
          message: `You can only archive lessons that are closed. Please close the lesson '${lesson.lessonName}' before archiving.`,
        },
        false,
      );
      return;
    } else if (
      action == GradeLessonStatus.open &&
      lesson.lessonStatus !== GradeLessonStatus.archived
    ) {
      this.utilsService.onShowAlert(
        {
          title: 'Lesson Not Archived',
          message: `You can only unarchive lessons that are archived. Please archive the lesson '${lesson.lessonName}' before unarchiving.`,
        },
        false,
      );
      return;
    }

    if (!lesson.groupId || !lesson.id) return;

    let dialogConfig = new MatDialogConfig();
    dialogConfig.data =
      action == GradeLessonStatus.archived
        ? {
            title: 'Archive Lesson',
            description: `Are you sure you want to archive the lesson '${lesson.lessonName}' for '${this.grade?.gradeName}' grade?`,
            action: 'Archive',
            buttonColor: 'bg-red-500',
            showType: AlertType.warning,
          }
        : {
            title: 'Unarchive Lesson',
            description: `Are you sure you want to unarchive the lesson '${lesson.lessonName}' for '${this.grade?.gradeName}' grade?`,
            action: 'Unarchive',
            buttonColor: 'bg-green-500',
            showType: AlertType.info,
          };

    this.utilsService
      .onRequestConfirmation(dialogConfig.data)
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          return this.store.dispatch(
            lessonActions.update({
              groupId: lesson.groupId!,
              lessonId: lesson.id!,
              gradeLesson: {
                ...lesson,
                lessonStatus: action,
              },
            }),
          );
        }
        return;
      });
  }
}
